import React from 'react';
import { Alert, Button, Table, Typography } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { SorterResult, TableCurrentDataSource } from 'antd/es/table/interface';
import { ReloadOutlined } from '@ant-design/icons';
import type { CampoOrdenEmpleados, EmpleadoResumen, OrdenEmpleados } from '../../api/empleadosApi';
import { EstadoEmpleadoTag } from './EstadoEmpleadoTag';
import { AvisoFotosSinPermiso, FotoEmpleado } from './FotoEmpleado';
import { fmtFecha, fmtNumero } from '../../fct/react';
import { fct } from '../../fct/tokens/tokens';

const { Text } = Typography;

/** Tamaños de página que admite el servidor (§8: de 10 a 100). */
export const TAMANOS_PAGINA = [10, 25, 50, 100];

export interface VistaListado {
  orden: OrdenEmpleados;
  /** Desde 1. */
  pagina: number;
  tamano: number;
}

export const VISTA_INICIAL: VistaListado = { orden: { campo: 'nombre', direccion: 'asc' }, pagina: 1, tamano: 25 };

const sinDato = <Text type="secondary">—</Text>;
const textoOVacio = (v?: string | null) => (v ? v : sinDato);

const CAMPOS_ORDEN: CampoOrdenEmpleados[] = ['nombre', 'email', 'puesto', 'delegacion', 'zona', 'alta', 'estado'];

interface Props {
  filas: EmpleadoResumen[];
  /** Total del filtro, el de todas las páginas. */
  total: number;
  vista: VistaListado;
  cargando: boolean;
  /** Mensaje de error de la última carga, o `null`. Con error no hay tabla: solo el aviso y el reintento. */
  error: string | null;
  /** Lo que se ve si no hay filas. */
  textoVacio: string;
  onVista: (vista: VistaListado) => void;
  onAbrir: (fila: EmpleadoResumen) => void;
  onReintentar: () => void;
}

/** Tabla del listado: orden por columna y paginación en el servidor, con estados vacío y de error. */
export const TablaEmpleados: React.FC<Props> = ({
  filas, total, vista, cargando, error, textoVacio, onVista, onAbrir, onReintentar,
}) => {
  const sentido = (campo: CampoOrdenEmpleados) =>
    (vista.orden.campo === campo ? (vista.orden.direccion === 'asc' ? 'ascend' : 'descend') : null);

  const columnas: ColumnsType<EmpleadoResumen> = [
    {
      title: 'Nombre', dataIndex: 'nombreCompleto', key: 'nombre', width: 240,
      render: (v: string, r: EmpleadoResumen) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <FotoEmpleado nombre={v} email={r.emailEmpresarial} />
          <span>{v}</span>
        </span>
      ),
    },
    { title: 'Email', dataIndex: 'emailEmpresarial', key: 'email', width: 260, render: textoOVacio },
    { title: 'Puesto', dataIndex: 'puesto', key: 'puesto', width: 200, render: textoOVacio },
    { title: 'Delegación', dataIndex: 'delegacion', key: 'delegacion', width: 180, render: textoOVacio },
    { title: 'Zona', dataIndex: 'zona', key: 'zona', width: 160, render: textoOVacio },
    { title: 'Estado', dataIndex: 'activo', key: 'estado', width: 120, render: (v: boolean) => <EstadoEmpleadoTag activo={v} /> },
    {
      title: 'Alta', dataIndex: 'fechaAlta', key: 'alta', width: 110, align: 'right', className: 'fct-num',
      render: (v?: string | null) => (v ? fmtFecha(v) : '—'),
    },
  ].map((c) => ({
    ...c,
    // Solo asc y desc: la tabla nunca queda sin orden, el servidor siempre ordena.
    sorter: true,
    sortOrder: sentido(c.key as CampoOrdenEmpleados),
    sortDirections: ['ascend', 'descend'] as ColumnsType<EmpleadoResumen>[number]['sortDirections'],
  })) as ColumnsType<EmpleadoResumen>;

  const alCambiar = (
    pag: TablePaginationConfig,
    _f: unknown,
    sorter: SorterResult<EmpleadoResumen> | SorterResult<EmpleadoResumen>[],
    extra: TableCurrentDataSource<EmpleadoResumen>,
  ) => {
    let orden = vista.orden;
    if (extra.action === 'sort') {
      const s = Array.isArray(sorter) ? sorter[0] : sorter;
      const campo = CAMPOS_ORDEN.find((c) => c === s?.columnKey);
      // Tras «descendente», Ant Design quita el orden (sin columna ni sentido); aquí la tabla siempre ordena,
      // así que ese clic vuelve a «ascendente» sobre la columna que estaba ordenada.
      orden = campo
        ? { campo, direccion: s.order === 'descend' ? 'desc' : 'asc' }
        : { campo: vista.orden.campo, direccion: 'asc' };
    }
    const tamano = pag.pageSize ?? vista.tamano;
    const cambiaOrden = orden.campo !== vista.orden.campo || orden.direccion !== vista.orden.direccion;
    // Otro orden o otro tamaño de página vuelven a la primera: la página que se miraba ya no significa lo mismo.
    const pagina = cambiaOrden || tamano !== vista.tamano ? 1 : (pag.current ?? vista.pagina);
    onVista({ orden, pagina, tamano });
  };

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="No se ha podido cargar el listado"
        description={error}
        action={<Button size="small" icon={<ReloadOutlined />} loading={cargando} onClick={onReintentar}>Reintentar</Button>}
      />
    );
  }

  return (
    <>
      <AvisoFotosSinPermiso />
      <Table<EmpleadoResumen>
        rowKey="idEmpleado"
        size="small"
        loading={cargando}
        columns={columnas}
        dataSource={filas}
        scroll={{ x: 1270 }}
        locale={{ emptyText: textoVacio }}
        onRow={(r) => ({
          style: { cursor: 'pointer' },
          tabIndex: 0,
          onClick: () => onAbrir(r),
          onKeyDown: (e) => { if (e.key === 'Enter') onAbrir(r); },
        })}
        onChange={alCambiar}
        pagination={{
          current: vista.pagina,
          pageSize: vista.tamano,
          total,
          pageSizeOptions: TAMANOS_PAGINA.map(String),
          showSizeChanger: true,
          showTotal: (t) => `${fmtNumero(t)} empleados`,
        }}
        style={{ background: fct.superficie.tarjeta }}
      />
    </>
  );
};
